import { createContext, useContext } from "react";
import { storeChangeSources, type ChangeEmitter, type Store } from "@/data/repositories";
import { CalendarModel } from "@/domain/calendar/CalendarModel";
import { SystemClock, type Clock } from "@/domain/clock";
import { ReportBuilder } from "@/domain/reports/ReportBuilder";
import { AccountService } from "@/services/AccountService";
import { AnalyticsService, type Environment } from "@/services/AnalyticsService";
import { HttpApi, type Api } from "@/services/ApiClient";
import { ArchiveService, StreamCompressor, type Compressor } from "@/services/ArchiveService";
import { EntryService } from "@/services/EntryService";
import { BrowserPushPlatform, type PushPlatform } from "@/services/PushPlatform";
import { ReminderService } from "@/services/ReminderService";
import { LocalNotificationReminderService } from "@/services/reminders/LocalNotificationReminderService";
import { CapacitorNotificationScheduler } from "@/services/reminders/CapacitorNotificationScheduler";
import type { ReminderBackend } from "@/services/reminders/types";
import { isNativeBuild } from "@/app/apiBase";
import { CapacitorLanguagePort, SystemLanguage } from "@/services/SystemLanguage";
import { TransferService } from "@/services/TransferService";
import { SubscriptionService } from "@/services/SubscriptionService";

export interface AppContainer {
  clock: Clock;
  store: Store;
  /** Stable array; pass it to useQuery so screens refresh after writes. */
  changeSources: readonly ChangeEmitter[];
  subscriptions: SubscriptionService;
  entries: EntryService;
  reports: ReportBuilder;
  calendar: CalendarModel;
  api: Api;
  account: AccountService;
  archive: ArchiveService;
  analytics: AnalyticsService;
  reminders: ReminderBackend;
  transfer: TransferService;
  /** The system's per-app language, kept equal to the app's (iOS); a no-op on the web. */
  systemLanguage: SystemLanguage;
}

const TEST_ENVIRONMENT: Environment = { userAgent: "test", webdriver: false, standalone: false, language: "en-US" };

export function createAppContainer(
  store: Store,
  clock: Clock = new SystemClock(),
  api: Api = new HttpApi(),
  compressor: Compressor = new StreamCompressor(),
  environment: Environment = TEST_ENVIRONMENT,
  pushPlatform: PushPlatform = new BrowserPushPlatform(),
): AppContainer {
  const changeSources = storeChangeSources(store);
  const account = new AccountService(api, store);
  const archive = new ArchiveService({ api, store, account, clock, compressor, changeSources });
  // The web build reminds through browser push; the native build schedules on-device local notifications.
  const reminders: ReminderBackend = isNativeBuild()
    ? new LocalNotificationReminderService({ scheduler: new CapacitorNotificationScheduler(), store, changeSources, language: environment.language })
    : new ReminderService({ api, store, clock, platform: pushPlatform, changeSources, language: environment.language });
  return {
    clock,
    store,
    changeSources,
    api,
    account,
    archive,
    transfer: new TransferService(api, store, archive, compressor),
    analytics: new AnalyticsService(api, store, clock, environment),
    reminders,
    systemLanguage: new SystemLanguage(isNativeBuild() ? new CapacitorLanguagePort() : null),
    subscriptions: new SubscriptionService(store, clock),
    entries: new EntryService(store, clock),
    reports: new ReportBuilder(),
    calendar: new CalendarModel(),
  };
}

export const AppContext = createContext<AppContainer | null>(null);

export function useApp(): AppContainer {
  const app = useContext(AppContext);
  if (!app) throw new Error("AppContext is missing; wrap the tree in <AppProviders>");
  return app;
}
